import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { ShieldCheck } from 'lucide-react';

const S = ({ n, title, children }) => (
  <section className="space-y-2">
    <h3 className="text-sm font-bold text-gray-900">{n}. {title}</h3>
    <div className="text-[13px] leading-relaxed text-gray-600 space-y-2">{children}</div>
  </section>
);

const UL = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export const TermsDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="terms-dialog">
      <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-orange-50 to-amber-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-left">
            <DialogTitle className="text-base font-extrabold text-gray-900">
              Termo de Ciência e Tratamento de Dados Pessoais
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              FOX SELLER — MoedasOnline.com
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>
      <ScrollArea className="max-h-[62vh]">
        <div className="px-6 py-5 space-y-5" data-testid="terms-content">
          <p className="text-[13px] leading-relaxed text-gray-600">
            Ao criar uma conta, realizar uma compra ou utilizar os serviços da FOX SELLER,
            disponibilizados por meio do domínio MoedasOnline.com, o usuário declara estar ciente de
            que seus dados pessoais poderão ser coletados, utilizados, armazenados, processados e
            conservados pela FOX SELLER, nos limites necessários à prestação dos serviços e em
            conformidade com a <b>Lei nº 13.709/2018 — Lei Geral de Proteção de Dados Pessoais (LGPD)</b>{' '}
            e demais normas aplicáveis.
          </p>
          <p className="text-[13px] leading-relaxed text-gray-600">
            Para os fins deste Termo, todas as referências à “FOX SELLER”, “Plataforma”, “nós” ou
            “nosso” referem-se ao serviço operado sob a marca FOX SELLER e disponibilizado através do
            domínio MoedasOnline.com.
          </p>

          <S n="1" title="Dados tratados">
            <p>
              Para funcionamento da Plataforma, poderão ser tratados dados fornecidos diretamente pelo
              usuário e dados relacionados à utilização dos serviços, incluindo, conforme aplicável:
            </p>
            <UL items={[
              'nome completo;',
              'CPF;',
              'data de nascimento;',
              'telefone e endereço de e-mail;',
              'identificadores e informações da conta;',
              'dados necessários à realização e identificação de pagamentos;',
              'histórico de pedidos, compras, recargas e transações;',
              'registros de acesso, endereço IP, data, horário e informações técnicas do dispositivo ou navegador;',
              'registros de atendimento e suporte;',
              'informações necessárias à prevenção de fraudes, segurança e cumprimento de obrigações legais ou regulatórias.',
            ]} />
            <p>
              A FOX SELLER buscará limitar a coleta e utilização dos dados ao que seja adequado,
              pertinente e necessário às respectivas finalidades.
            </p>
          </S>

          <S n="2" title="Finalidades do tratamento">
            <p>Os dados pessoais poderão ser tratados para:</p>
            <UL items={[
              'a) criar, identificar, autenticar e administrar a conta do usuário;',
              'b) processar pedidos, pagamentos, recargas e demais operações solicitadas;',
              'c) confirmar a identidade do usuário quando necessário;',
              'd) prevenir e detectar fraudes, abusos, acessos indevidos e atividades incompatíveis com a segurança da Plataforma;',
              'e) prestar atendimento e suporte;',
              'f) manter registros de operações e transações;',
              'g) realizar controles internos, conciliações, auditorias e investigações de segurança;',
              'h) produzir evidências e preservar registros necessários ao exercício regular de direitos em processos judiciais, administrativos ou arbitrais;',
              'i) cumprir obrigações legais, regulatórias, fiscais, contábeis e determinações de autoridades competentes;',
              'j) garantir a segurança, integridade e funcionamento dos sistemas e serviços da FOX SELLER.',
            ]} />
          </S>

          <S n="3" title="Bases legais">
            <p>
              O tratamento dos dados pessoais será realizado de acordo com as bases legais aplicáveis a
              cada finalidade, incluindo, quando cabível:
            </p>
            <UL items={[
              'execução de contrato ou procedimentos relacionados à contratação dos serviços;',
              'cumprimento de obrigação legal ou regulatória;',
              'exercício regular de direitos;',
              'legítimo interesse, observados os direitos e liberdades fundamentais do titular;',
              'prevenção à fraude e segurança das operações, quando aplicável;',
              'consentimento do titular, quando essa for a base legal adequada.',
            ]} />
            <p>
              A eventual revogação de um consentimento não impedirá a conservação ou o tratamento de
              informações quando houver outra base legal que autorize ou exija sua manutenção.
            </p>
          </S>

          <S n="4" title="Armazenamento e conservação">
            <p>
              Os dados e registros poderão ser armazenados durante o período necessário para cumprir as
              finalidades para as quais foram coletados e pelos prazos necessários ao cumprimento de
              obrigações legais ou regulatórias, exercício regular de direitos, prevenção de fraudes,
              segurança e auditoria.
            </p>
            <p>
              Encerrada a necessidade de tratamento, os dados serão eliminados, anonimizados ou
              conservados exclusivamente nas hipóteses autorizadas pela legislação aplicável. A exclusão
              da conta pelo usuário não implica necessariamente a eliminação imediata de todos os
              registros relacionados às operações realizadas anteriormente, quando sua conservação for
              necessária ou permitida pela legislação.
            </p>
          </S>

          <S n="5" title="Compartilhamento de dados">
            <p>
              Quando necessário para prestação dos serviços, os dados poderão ser compartilhados, de
              forma limitada às respectivas finalidades, com fornecedores e parceiros responsáveis por
              atividades como:
            </p>
            <UL items={[
              'processamento de pagamentos;',
              'infraestrutura e hospedagem;',
              'segurança e prevenção a fraudes;',
              'autenticação e comunicação;',
              'serviços contábeis, jurídicos ou tecnológicos;',
              'cumprimento de determinações legais ou de autoridades competentes.',
            ]} />
            <p>
              A FOX SELLER buscará assegurar que o compartilhamento de dados seja realizado de maneira
              compatível com as finalidades que justificaram o tratamento e com a legislação aplicável.
            </p>
          </S>

          <S n="6" title="Segurança, registros e auditoria">
            <p>
              A FOX SELLER adotará medidas técnicas e administrativas destinadas à proteção dos dados
              pessoais contra acessos não autorizados e situações acidentais ou ilícitas de destruição,
              perda, alteração, divulgação ou tratamento inadequado.
            </p>
            <p>
              Para proteção dos usuários e da Plataforma, poderão ser mantidos registros técnicos e
              históricos das operações realizadas, incluindo logs de acesso, autenticações, alterações
              relevantes, transações, pagamentos, pedidos, eventos de segurança e demais registros
              necessários à rastreabilidade das operações.
            </p>
            <p>
              Essas informações poderão ser utilizadas para prevenção e investigação de fraudes,
              resolução de disputas, auditorias internas, comprovação de operações, segurança da
              informação e exercício regular de direitos.
            </p>
          </S>

          <S n="7" title="Direitos do titular">
            <p>
              Nos termos da LGPD, o usuário poderá exercer os direitos que lhe forem aplicáveis,
              incluindo solicitar:
            </p>
            <UL items={[
              'confirmação da existência de tratamento;',
              'acesso aos seus dados pessoais;',
              'correção de dados incompletos, inexatos ou desatualizados;',
              'informações sobre compartilhamento;',
              'anonimização, bloqueio ou eliminação de dados tratados em desconformidade com a legislação;',
              'eliminação dos dados tratados com fundamento no consentimento, observadas as hipóteses legais de conservação;',
              'revogação do consentimento, quando aplicável;',
              'demais direitos previstos na legislação de proteção de dados.',
            ]} />
            <p>
              Por motivos de segurança, a FOX SELLER poderá solicitar informações adicionais para
              confirmar a identidade do titular antes de atender determinadas solicitações.
            </p>
          </S>

          <S n="8" title="Responsabilidade do usuário">
            <p>
              O usuário declara que as informações fornecidas durante seu cadastro e utilização da
              Plataforma são verdadeiras, completas e atualizadas, responsabilizando-se pela veracidade
              dos dados informados. O usuário deverá manter seus dados cadastrais atualizados e proteger
              adequadamente suas credenciais de acesso.
            </p>
          </S>

          <S n="9" title="Registro eletrônico do aceite">
            <p>
              Ao selecionar a opção de aceite apresentada durante o cadastro, o usuário declara que leu,
              compreendeu e está ciente deste Termo. Para fins de segurança, auditoria e comprovação, a
              FOX SELLER poderá registrar eletronicamente o aceite, incluindo informações como:
            </p>
            <UL items={[
              'identificação do usuário;',
              'data e horário do aceite;',
              'versão do documento aceita;',
              'endereço IP;',
              'identificadores da sessão;',
              'informações técnicas relacionadas ao dispositivo ou navegador, quando necessárias e aplicáveis.',
            ]} />
            <p>
              Esses registros poderão ser preservados para comprovar a manifestação realizada pelo
              usuário e proteger os direitos do usuário e da FOX SELLER.
            </p>
          </S>

          <S n="10" title="Ciência do usuário">
            <p>Ao concluir seu cadastro e aceitar este Termo, o usuário declara estar ciente de que:</p>
            <UL items={[
              'I. seus dados serão tratados para as finalidades descritas neste documento;',
              'II. determinados tratamentos são necessários para a prestação dos serviços, processamento das operações, segurança, prevenção de fraudes e cumprimento de obrigações legais;',
              'III. determinados registros poderão ser conservados após o encerramento da conta quando houver fundamento legal para sua manutenção;',
              'IV. poderá exercer os direitos assegurados pela legislação de proteção de dados, observadas as hipóteses e limitações previstas em lei.',
            ]} />
          </S>

          <div className="pt-2 border-t text-center">
            <p className="text-xs font-bold text-gray-800">FOX SELLER</p>
            <p className="text-[11px] text-gray-400">Plataforma disponível em MoedasOnline.com</p>
          </div>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
);

export default TermsDialog;
